/**
 * UsageTracker - Service for tracking token usage and calculating costs
 * Supports modality-aware pricing for OpenAI Realtime API
 */

// Pricing profiles (USD per 1K tokens)
const PRICING = {
  text: {
    input: 0.004,
    cached_input: 0.0004,
    output: 0.024
  },
  audio: {
    input: 0.032,
    cached_input: 0.0004,
    output: 0.064
  }
};

export class UsageTracker {
  constructor(conversationMode = 'voiceText') {
    this.conversationMode = conversationMode; // 'text', 'voice', 'voiceText'
    this.totalUsage = {
      newInputTokens: 0,
      cachedTokens: 0,
      outputTokens: 0,
      newInputTextTokens: 0,
      newInputAudioTokens: 0,
      cachedTextTokens: 0,
      cachedAudioTokens: 0,
      outputTextTokens: 0,
      outputAudioTokens: 0,
      totalCost: 0,
      usageEventCount: 0,
      usageEvents: []
    };
  }

  /**
   * Update usage totals with new token data
   * @param {Object} usageData - Token usage data from OpenAI response
   * @param {number} usageData.inputTokens - New input tokens
   * @param {number} usageData.cachedTokens - Cached input tokens
   * @param {number} usageData.outputTokens - Output tokens
   */
  updateUsage(usageData) {
    try {
      // Validate usage data
      if (!this.validateUsageData(usageData)) {
        console.warn('Invalid usage data received:', usageData);
        return;
      }

      const normalizedUsage = this.normalizeUsageData(usageData);

      let totalCost;
      if (normalizedUsage.hasDetailedBreakdown) {
        totalCost =
          this.calculateCost(normalizedUsage.newInputTextTokens, 'input', 'text') +
          this.calculateCost(normalizedUsage.newInputAudioTokens, 'input', 'audio') +
          this.calculateCost(normalizedUsage.cachedTextTokens, 'cached_input', 'text') +
          this.calculateCost(normalizedUsage.cachedAudioTokens, 'cached_input', 'audio') +
          this.calculateCost(normalizedUsage.outputTextTokens, 'output', 'text') +
          this.calculateCost(normalizedUsage.outputAudioTokens, 'output', 'audio');
      } else {
        const modality = this.determineModality();
        totalCost =
          this.calculateCost(normalizedUsage.newInputTokens, 'input', modality) +
          this.calculateCost(normalizedUsage.cachedTokens, 'cached_input', modality) +
          this.calculateCost(normalizedUsage.outputTokens, 'output', modality);
      }

      // Update running totals
      this.totalUsage.newInputTokens += normalizedUsage.newInputTokens;
      this.totalUsage.cachedTokens += normalizedUsage.cachedTokens;
      this.totalUsage.outputTokens += normalizedUsage.outputTokens;
      this.totalUsage.newInputTextTokens += normalizedUsage.newInputTextTokens;
      this.totalUsage.newInputAudioTokens += normalizedUsage.newInputAudioTokens;
      this.totalUsage.cachedTextTokens += normalizedUsage.cachedTextTokens;
      this.totalUsage.cachedAudioTokens += normalizedUsage.cachedAudioTokens;
      this.totalUsage.outputTextTokens += normalizedUsage.outputTextTokens;
      this.totalUsage.outputAudioTokens += normalizedUsage.outputAudioTokens;
      this.totalUsage.totalCost += totalCost;
      if (normalizedUsage.usageEvent) {
        this.totalUsage.usageEvents.push(normalizedUsage.usageEvent);
        this.totalUsage.usageEventCount += 1;
      }

      // console.log('Usage updated:', {
      //   tokens: { input: inputTokens, cached: cachedTokens, output: outputTokens },
      //   cost: totalCost,
      //   modality,
      //   totals: this.totalUsage
      // });

    } catch (error) {
      console.error('Error updating usage:', error);
      // Continue operation without breaking
    }
  }

  normalizeUsageData(usageData) {
    const {
      inputTokens = 0,
      cachedTokens = 0,
      outputTokens = 0,
      inputTextTokens,
      inputAudioTokens,
      cachedTextTokens,
      cachedAudioTokens,
      outputTextTokens,
      outputAudioTokens,
      usageEvent
    } = usageData;

    const hasDetailedBreakdown = [
      inputTextTokens,
      inputAudioTokens,
      cachedTextTokens,
      cachedAudioTokens,
      outputTextTokens,
      outputAudioTokens
    ].some((value) => value !== undefined);

    if (!hasDetailedBreakdown) {
      return {
        newInputTokens: Math.max(0, inputTokens - cachedTokens),
        cachedTokens,
        outputTokens,
        newInputTextTokens: 0,
        newInputAudioTokens: 0,
        cachedTextTokens: 0,
        cachedAudioTokens: 0,
        outputTextTokens: 0,
        outputAudioTokens: 0,
        usageEvent,
        hasDetailedBreakdown: false
      };
    }

    const safeInputTextTokens = inputTextTokens || 0;
    const safeInputAudioTokens = inputAudioTokens || 0;
    const safeCachedTextTokens = cachedTextTokens || 0;
    const safeCachedAudioTokens = cachedAudioTokens || 0;
    const safeOutputTextTokens = outputTextTokens || 0;
    const safeOutputAudioTokens = outputAudioTokens || 0;
    const newInputText = Math.max(0, safeInputTextTokens - safeCachedTextTokens);
    const newInputAudio = Math.max(0, safeInputAudioTokens - safeCachedAudioTokens);

    return {
      newInputTokens: newInputText + newInputAudio,
      cachedTokens: safeCachedTextTokens + safeCachedAudioTokens,
      outputTokens: safeOutputTextTokens + safeOutputAudioTokens,
      newInputTextTokens: newInputText,
      newInputAudioTokens: newInputAudio,
      cachedTextTokens: safeCachedTextTokens,
      cachedAudioTokens: safeCachedAudioTokens,
      outputTextTokens: safeOutputTextTokens,
      outputAudioTokens: safeOutputAudioTokens,
      usageEvent,
      hasDetailedBreakdown: true
    };
  }

  /**
   * Calculate cost for tokens based on type and modality
   * @param {number} tokenCount - Number of tokens
   * @param {string} tokenType - 'input', 'cached_input', or 'output'
   * @param {string} modality - 'text' or 'audio'
   * @returns {number} Cost in USD
   */
  calculateCost(tokenCount, tokenType, modality) {
    try {
      if (tokenCount <= 0) return 0;

      const rate = PRICING[modality]?.[tokenType];
      if (typeof rate !== 'number') {
        console.warn(`Unknown pricing for ${modality}/${tokenType}`);
        return 0;
      }

      return (tokenCount / 1000) * rate;
    } catch (error) {
      console.error('Error calculating cost:', error);
      return 0;
    }
  }

  /**
   * Determine modality based on conversation mode
   * @returns {string} 'text' or 'audio'
   */
  determineModality() {
    // If conversation mode includes voice/audio, use audio pricing
    if (this.conversationMode === 'voice' || this.conversationMode === 'voiceText') {
      return 'audio';
    }
    return 'text';
  }

  /**
   * Validate usage data structure
   * @param {Object} usageData - Usage data to validate
   * @returns {boolean} True if valid
   */
  validateUsageData(usageData) {
    if (!usageData || typeof usageData !== 'object') {
      return false;
    }

    const {
      inputTokens,
      cachedTokens,
      outputTokens,
      inputTextTokens,
      inputAudioTokens,
      cachedTextTokens,
      cachedAudioTokens,
      outputTextTokens,
      outputAudioTokens,
      usageEvent
    } = usageData;
    
    // Check that token counts are valid numbers (allow 0)
    const isValidToken = (value) => 
      typeof value === 'number' && value >= 0 && Number.isInteger(value);

    return (
      (inputTokens === undefined || isValidToken(inputTokens)) &&
      (cachedTokens === undefined || isValidToken(cachedTokens)) &&
      (outputTokens === undefined || isValidToken(outputTokens)) &&
      (inputTextTokens === undefined || isValidToken(inputTextTokens)) &&
      (inputAudioTokens === undefined || isValidToken(inputAudioTokens)) &&
      (cachedTextTokens === undefined || isValidToken(cachedTextTokens)) &&
      (cachedAudioTokens === undefined || isValidToken(cachedAudioTokens)) &&
      (outputTextTokens === undefined || isValidToken(outputTextTokens)) &&
      (outputAudioTokens === undefined || isValidToken(outputAudioTokens)) &&
      (usageEvent === undefined || (usageEvent && typeof usageEvent === 'object'))
    );
  }

  /**
   * Get current total usage
   * @returns {Object} Copy of current usage totals
   */
  getTotalUsage() {
    return { ...this.totalUsage };
  }

  /**
   * Reset usage tracking for new conversation
   */
  reset() {
    this.totalUsage = {
      newInputTokens: 0,
      cachedTokens: 0,
      outputTokens: 0,
      newInputTextTokens: 0,
      newInputAudioTokens: 0,
      cachedTextTokens: 0,
      cachedAudioTokens: 0,
      outputTextTokens: 0,
      outputAudioTokens: 0,
      totalCost: 0,
      usageEventCount: 0,
      usageEvents: []
    };
    // console.log('Usage tracker reset');
  }

  /**
   * Get usage summary for logging
   * @returns {Object} Usage summary with metadata
   */
  getUsageSummary() {
    const totals = this.getTotalUsage();
    return {
      ...totals,
      totalTokens: totals.newInputTokens + totals.cachedTokens + totals.outputTokens,
      modality: this.determineModality(),
      conversationMode: this.conversationMode
    };
  }
}

export default UsageTracker;
